import React, { useState } from 'react';
import IMAGES from './img';

const LegalPage = () => {
  const [activeTab, setActiveTab] = useState('terms');

  return (
    <div className="min-h-screen bg-sky-50 py-12 px-4 md:px-8 font-sans">
      <div className="max-w-5xl mx-auto">
        
        {/* Header Section - Pure Logo integration */}
        <div className="flex flex-col items-center mb-10">
          <div className="mb-6">
            <img 
              src={IMAGES.raftLogo} 
              alt="Stranded on a Raft Logo" 
              className="h-32 md:h-40 w-auto object-contain"
            />
          </div>
          <div className="flex items-center gap-2 mt-3 text-sky-600">
            <div className="h-px w-8 bg-sky-300"></div>
            <span className="font-bold tracking-[0.2em] text-[10px] md:text-xs uppercase text-center">Legal Compliance Portal</span>
            <div className="h-px w-8 bg-sky-300"></div>
          </div>
        </div>

        {/* Navigation Selector */}
        <div className="flex justify-center mb-10">
          <div className="join shadow-xl shadow-sky-200/40 border border-sky-100 p-1 bg-white rounded-2xl">
            <button 
              className={`join-item btn btn-md md:btn-lg px-8 md:px-12 transition-all border-none rounded-xl ${activeTab === 'terms' ? 'bg-sky-500 text-white hover:bg-sky-600 shadow-md shadow-sky-200' : 'bg-transparent text-sky-700 hover:bg-sky-50'}`}
              onClick={() => setActiveTab('terms')}
            >
              Terms & Conditions
            </button>
            <button 
              className={`join-item btn btn-md md:btn-lg px-8 md:px-12 transition-all border-none rounded-xl ${activeTab === 'privacy' ? 'bg-sky-500 text-white hover:bg-sky-600 shadow-md shadow-sky-200' : 'bg-transparent text-sky-700 hover:bg-sky-50'}`}
              onClick={() => setActiveTab('privacy')}
            >
              Privacy Policy
            </button>
          </div>
        </div>

        {/* Document Card */}
        <div className="card bg-white shadow-2xl shadow-sky-100 border border-sky-50 mb-8 rounded-3xl overflow-hidden">
          <div className="card-body p-8 md:p-12 lg:p-16">
            
            {activeTab === 'terms' ? (
              <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center border-b border-sky-50 pb-6 mb-8 gap-4">
                  <h2 className="text-2xl font-black text-sky-900 uppercase">Terms & Conditions</h2>
                  <span className="badge badge-info bg-sky-50 text-sky-700 border-sky-100 p-3 text-xs font-bold uppercase tracking-tight italic text-center">Last Revision: Feb 2026</span>
                </div>

                <article className="prose prose-slate max-w-none text-slate-600 leading-relaxed">
                  <p className="bg-sky-50/50 p-6 rounded-2xl border-l-4 border-sky-400 font-medium italic text-sky-800">
                    By downloading or using the game, these terms automatically apply to you. Please read them carefully before starting your survival journey.
                  </p>

                  <h3 className="text-sky-900 font-bold mt-8 italic border-b border-sky-50 w-fit pr-4">1. Intellectual Property</h3>
                  <p>All trademarks, copyrights, and intellectual property rights related to Stranded on a Raft belong to <strong>Rinexus Games</strong>. You may not copy, modify, or attempt to extract the source code of the game or its assets.</p>

                  <h3 className="text-sky-900 font-bold mt-8 italic border-b border-sky-50 w-fit pr-4">2. Security Warning</h3>
                  <div className="alert shadow-sm border-none bg-sky-900 text-sky-50 my-6 rounded-2xl">
                    <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                    <span className="text-sm"><strong>Warning:</strong> Do not jailbreak or root your device. This compromises security and may cause the game to malfunction or lose connectivity to your account data.</span>
                  </div>

                  <h3 className="text-sky-900 font-bold mt-8 italic border-b border-sky-50 w-fit pr-4">3. Data & Connectivity</h3>
                  <p>The game requires an active internet connection to sync progress and assets. Rinexus Games is not responsible for data charges incurred through your mobile service provider.</p>
                </article>
              </div>
            ) : (
              <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center border-b border-sky-50 pb-6 mb-8 gap-4">
                  <h2 className="text-2xl font-black text-sky-900 uppercase">Privacy Policy</h2>
                  <span className="badge badge-outline border-sky-200 text-sky-600 p-3 text-xs font-bold uppercase tracking-widest italic">Data Usage</span>
                </div>

                <article className="prose prose-slate max-w-none text-slate-600 leading-relaxed">
                  <p>Rinexus Games built Stranded on a Raft as a <strong>Freemium game</strong>. To provide account synchronization and personalized content, we use specific cloud infrastructures.</p>

                  <h3 className="text-sky-900 font-bold mt-8 italic border-b border-sky-50 w-fit pr-4 text-lg">Infrastructure & Data Storage</h3>
                  <p>We rely on industry-standard providers to manage your player experience safely:</p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-8">
                    <div className="p-6 bg-sky-50/30 rounded-2xl border border-sky-100">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-2 h-2 rounded-full bg-orange-400"></div>
                        <h4 className="font-black text-sky-900 text-xs uppercase tracking-widest">Firebase (Google)</h4>
                      </div>
                      <p className="text-sm text-sky-800 leading-relaxed">
                        Used for secure user authentication and database management. This stores your <strong>account data, received gifts, and unlocked cosmetics</strong> to ensure your progress is saved across sessions.
                      </p>
                    </div>
                    <div className="p-6 bg-sky-50/30 rounded-2xl border border-sky-100">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
                        <h4 className="font-black text-sky-900 text-xs uppercase tracking-widest">Supabase</h4>
                      </div>
                      <p className="text-sm text-sky-800 leading-relaxed">
                        Acts as our primary <strong>Asset Storage</strong>. Supabase hosts high-quality cosmetic assets and game files that are downloaded to your device to customize your raft and character.
                      </p>
                    </div>
                  </div>

                  <h3 className="text-sky-900 font-bold mt-8 italic border-b border-sky-50 w-fit pr-4">Third-Party Analytics</h3>
                  <p>In addition to the above, we use <strong>AdMob</strong> and <strong>Firebase Analytics</strong> to monitor app stability and provide advertisements that keep the game free for all players.</p>

                  <h3 className="text-sky-900 font-bold mt-8 italic border-b border-sky-50 w-fit pr-4">Children's Privacy</h3>
                  <p>We do not knowingly collect personal information from children under 13. If you are a parent and believe data has been collected, please contact us immediately.</p>
                </article>
              </div>
            )}

            {/* Support/Contact Footer */}
            <div className="divider mt-16 mb-8 text-sky-200 uppercase text-[10px] tracking-[0.3em] font-black italic">Contact Support</div>
            <div className="bg-sky-900 rounded-3xl p-8 flex flex-col md:flex-row justify-between items-center gap-6 shadow-xl">
              <div className="text-center md:text-left">
                <p className="text-white font-black text-xl uppercase tracking-tight italic">Technical Support</p>
                <p className="text-sky-300 text-sm font-medium">For account issues or legal inquiries, reach out to our team.</p>
              </div>
              <a href="mailto:business@rinexusgames.com" className="btn bg-sky-500 hover:bg-sky-400 text-white border-none px-12 rounded-xl font-bold uppercase shadow-lg shadow-sky-400/20 transition-all active:scale-95">
                Email Business
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Footer */}
        <div className="flex flex-col items-center gap-3 py-8 text-center">
          <p className="text-sky-800/40 text-xs font-bold uppercase tracking-widest">
            © {new Date().getFullYear()} Rinexus Games. Stranded on a Raft.
          </p>
          <div className="flex gap-6">
             <a href="/" className="text-xs text-sky-900/60 hover:text-sky-500 transition-colors font-bold uppercase tracking-tighter decoration-sky-200 underline underline-offset-8">Return Home</a>
             <a href="https://ko-fi.com/rinexusgames" target="_blank" rel="noreferrer" className="text-xs text-sky-900/60 hover:text-sky-500 transition-colors font-bold uppercase tracking-tighter decoration-sky-200 underline underline-offset-8">Support Development</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalPage;